import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { NAV, NAV_BY_KEY, NAV_ITEM_BY_TO, groupOrder } from "@/lib/nav";
import { fetchAdminSettings, adminUpdateSettings } from "@/lib/api";
import {
  GripVertical, Eye, EyeOff, ChevronDown, ChevronRight, Save, RotateCcw, CornerUpRight, Loader2,
} from "lucide-react";

const CONTEXTS = [
  { key: "header", label: "Header Menu" },
  { key: "footer", label: "Footer" },
  { key: "directory", label: "Directory (/explore)" },
];

const clone = (o) => JSON.parse(JSON.stringify(o || {}));

// Items currently assigned to a group under a context config (incl. hidden).
function itemOrderFor(ctxCfg, key) {
  const base = NAV_BY_KEY[key];
  const gc = (ctxCfg.groups && ctxCfg.groups[key]) || {};
  const referenced = new Set();
  Object.values(ctxCfg.groups || {}).forEach((g) => (g.itemsOrder || []).forEach((to) => referenced.add(to)));
  const order = [];
  (gc.itemsOrder || base.items.map((it) => it.to)).forEach((to) => {
    if (NAV_ITEM_BY_TO[to] && !order.includes(to)) order.push(to);
  });
  base.items.forEach((it) => { if (!referenced.has(it.to) && !order.includes(it.to)) order.push(it.to); });
  return order;
}

export default function AdminNavigation() {
  const [cfg, setCfg] = useState(null);
  const [ctx, setCtx] = useState("header");
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState(false);
  const [dropTarget, setDropTarget] = useState(null); // {kind:'group'|'item', key, to?}
  const drag = useRef(null); // { type:'group'|'item', key, to? }

  useEffect(() => {
    fetchAdminSettings().then((d) => setCfg(d.nav_config || {})).catch(() => setCfg({}));
  }, []);

  if (!cfg) return <div className="flex items-center gap-2 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  const ctxCfg = cfg[ctx] || {};
  const keys = groupOrder(ctxCfg);

  // Mutate the current context's config immutably.
  const update = (fn) => {
    const next = clone(cfg);
    next[ctx] = next[ctx] || {};
    next[ctx].groups = next[ctx].groups || {};
    fn(next[ctx]);
    setCfg(next);
  };
  const ensureGroup = (c, key) => { c.groups[key] = c.groups[key] || {}; return c.groups[key]; };

  const reorderGroups = (newKeys) => update((c) => { c.order = newKeys; });
  const reorderItems = (key, tos) => update((c) => { ensureGroup(c, key).itemsOrder = tos; });
  const toggleGroup = (key) => update((c) => { const g = ensureGroup(c, key); g.hidden = !g.hidden; });
  const toggleItem = (key, to) => update((c) => {
    const g = ensureGroup(c, key); g.items = g.items || {}; g.items[to] = g.items[to] || {};
    g.items[to].hidden = !g.items[to].hidden;
  });
  const renameGroup = (key, label) => update((c) => { ensureGroup(c, key).label = label; });
  const renameItem = (key, to, label) => update((c) => {
    const g = ensureGroup(c, key); g.items = g.items || {}; g.items[to] = g.items[to] || {}; g.items[to].label = label;
  });
  // Append `to` to the end of `toKey`, removing it from `fromKey`.
  const moveItem = (fromKey, to, toKey) => update((c) => {
    if (fromKey === toKey) return;
    ensureGroup(c, fromKey).itemsOrder = itemOrderFor(c, fromKey).filter((x) => x !== to);
    const dest = itemOrderFor(c, toKey).filter((x) => x !== to);
    dest.push(to);
    ensureGroup(c, toKey).itemsOrder = dest;
  });
  // Insert `to` before `beforeTo` inside `toKey` (moving from `fromKey`).
  const moveItemBefore = (fromKey, to, toKey, beforeTo) => update((c) => {
    if (fromKey !== toKey) ensureGroup(c, fromKey).itemsOrder = itemOrderFor(c, fromKey).filter((x) => x !== to);
    const dest = itemOrderFor(c, toKey).filter((x) => x !== to);
    let idx = beforeTo ? dest.indexOf(beforeTo) : -1;
    if (idx < 0) idx = dest.length;
    dest.splice(idx, 0, to);
    ensureGroup(c, toKey).itemsOrder = dest;
  });

  const resetContext = () => { const next = clone(cfg); delete next[ctx]; setCfg(next); };

  const save = async () => {
    setSaving(true);
    try {
      await adminUpdateSettings({ nav_config: cfg });
      toast("Navigation saved", { description: "Refresh the public site to see the changes." });
    } catch (e) {
      toast("Could not save", { description: e?.response?.data?.detail || "Please try again." });
    } finally { setSaving(false); }
  };

  const gLabel = (key) => {
    const gc = (ctxCfg.groups && ctxCfg.groups[key]) || {};
    const base = NAV_BY_KEY[key];
    return { en: gc.label?.en ?? base.label.en, hi: gc.label?.hi ?? base.label.deva };
  };
  const iLabel = (key, to) => {
    const ic = (ctxCfg.groups && ctxCfg.groups[key]?.items?.[to]) || {};
    const bi = NAV_ITEM_BY_TO[to];
    return { en: ic.label?.en ?? bi.en, hi: ic.label?.hi ?? bi.deva };
  };

  // ---------- Drag & drop ----------
  const clearDrag = () => { drag.current = null; setDropTarget(null); };

  const onGroupDrop = (targetKey) => {
    const d = drag.current;
    clearDrag();
    if (!d) return;
    if (d.type === "group") {
      if (d.key === targetKey) return;
      const ks = keys.filter((k) => k !== d.key);
      const idx = ks.indexOf(targetKey);
      ks.splice(idx < 0 ? ks.length : idx, 0, d.key);
      reorderGroups(ks);
    } else if (d.type === "item") {
      // Dropped onto the group shell → append to the end of that group.
      moveItem(d.key, d.to, targetKey);
      if (d.key !== targetKey) setExpanded((e) => ({ ...e, [targetKey]: true }));
    }
  };

  const onItemDrop = (targetKey, targetTo) => {
    const d = drag.current;
    clearDrag();
    if (!d || d.type !== "item") return;
    if (d.key === targetKey && d.to === targetTo) return;
    moveItemBefore(d.key, d.to, targetKey, targetTo);
    if (d.key !== targetKey) setExpanded((e) => ({ ...e, [targetKey]: true }));
  };

  return (
    <div data-testid="admin-navigation">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif-latin text-3xl text-ink">Navigation Builder</h1>
          <p className="mt-1 text-sm text-ink-3">Reorder, regroup, rename, hide or show menu items — separately for the header, footer and directory. Old links keep working; this only changes the menus.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetContext} data-testid="nav-reset" className="inline-flex items-center gap-1.5 border border-line px-3 py-2 text-sm text-ink-2 transition-colors hover:text-saffron">
            <RotateCcw className="h-4 w-4" /> Reset this tab
          </button>
          <button onClick={save} disabled={saving} data-testid="nav-save" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
          </button>
        </div>
      </div>

      <div className="mt-6 flex gap-1 border-b border-line">
        {CONTEXTS.map((c) => (
          <button key={c.key} onClick={() => setCtx(c.key)} data-testid={`nav-tab-${c.key}`}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm transition-colors ${ctx === c.key ? "border-saffron text-saffron" : "border-transparent text-ink-2 hover:text-ink"}`}>
            {c.label}
          </button>
        ))}
      </div>

      <p className="mt-4 text-xs text-ink-3">Drag the <GripVertical className="inline h-3.5 w-3.5" /> handle of an item and drop it onto another item (to place it there) or onto a group (to add it at the end). Drag a group's handle to reorder groups. The “Move to” menu still works as a fallback.</p>

      <div className="mt-4 space-y-3">
        {keys.map((key) => {
          const groupHidden = (ctxCfg.groups && ctxCfg.groups[key]?.hidden) || false;
          const gl = gLabel(key);
          const items = itemOrderFor(ctxCfg, key);
          const isOpen = expanded[key];
          const isGroupTarget = dropTarget?.kind === "group" && dropTarget.key === key;
          return (
            <div key={key}
              onDragOver={(e) => { if (drag.current) { e.preventDefault(); setDropTarget({ kind: "group", key }); } }}
              onDragLeave={(e) => { if (e.currentTarget === e.target) setDropTarget((t) => (t?.kind === "group" && t.key === key ? null : t)); }}
              onDrop={(e) => { e.preventDefault(); onGroupDrop(key); }}
              className={`border bg-surface transition-colors ${groupHidden ? "border-line/60 opacity-60" : "border-line"} ${isGroupTarget ? "ring-2 ring-saffron/60" : ""}`}
              data-testid={`nav-group-${key}`}>
              <div className="flex items-center gap-2 p-3">
                <span draggable
                  onDragStart={() => { drag.current = { type: "group", key }; }}
                  onDragEnd={clearDrag}
                  className="cursor-grab active:cursor-grabbing" data-testid={`nav-group-drag-${key}`}>
                  <GripVertical className="h-4 w-4 shrink-0 text-ink-3" />
                </span>
                <button onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))} className="text-ink-3" data-testid={`nav-group-toggle-${key}`}>
                  {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
                <input value={gl.hi} onChange={(e) => renameGroup(key, { en: gl.en, hi: e.target.value })}
                  className="w-28 border border-line bg-parchment px-2 py-1 font-deva text-sm text-ink focus:border-saffron focus:outline-none" aria-label="Hindi label" />
                <input value={gl.en} onChange={(e) => renameGroup(key, { en: e.target.value, hi: gl.hi })}
                  className="w-40 border border-line bg-parchment px-2 py-1 text-sm text-ink focus:border-saffron focus:outline-none" aria-label="English label" />
                <span className="ml-1 text-xs text-ink-3">{items.length} items</span>
                <button onClick={() => toggleGroup(key)} data-testid={`nav-group-hide-${key}`} className={`ml-auto inline-flex items-center gap-1.5 px-2 py-1 text-xs transition-colors ${groupHidden ? "text-ink-3 hover:text-saffron" : "text-saffron hover:text-ink-2"}`}>
                  {groupHidden ? <><EyeOff className="h-4 w-4" /> Hidden</> : <><Eye className="h-4 w-4" /> Visible</>}
                </button>
              </div>

              {isOpen && (
                <div className="border-t border-line p-2 pl-8">
                  {items.map((to) => {
                    const il = iLabel(key, to);
                    const itemHidden = (ctxCfg.groups && ctxCfg.groups[key]?.items?.[to]?.hidden) || false;
                    const bi = NAV_ITEM_BY_TO[to];
                    const isItemTarget = dropTarget?.kind === "item" && dropTarget.key === key && dropTarget.to === to;
                    return (
                      <div key={to}
                        onDragOver={(e) => { if (drag.current?.type === "item") { e.preventDefault(); e.stopPropagation(); setDropTarget({ kind: "item", key, to }); } }}
                        onDrop={(e) => { if (drag.current?.type === "item") { e.preventDefault(); e.stopPropagation(); onItemDrop(key, to); } }}
                        className={`mb-1.5 flex flex-wrap items-center gap-2 border bg-parchment p-2 transition-colors ${itemHidden ? "border-line/60 opacity-55" : "border-line"} ${isItemTarget ? "border-saffron ring-1 ring-saffron/50" : ""}`}
                        data-testid={`nav-item-${key}-${to.replace(/\//g, "")}`}>
                        <span draggable
                          onDragStart={(e) => { drag.current = { type: "item", key, to }; e.stopPropagation(); }}
                          onDragEnd={clearDrag}
                          className="cursor-grab active:cursor-grabbing" data-testid={`nav-item-drag-${to.replace(/\//g, "")}`}>
                          <GripVertical className="h-4 w-4 shrink-0 text-ink-3" />
                        </span>
                        <input value={il.hi} onChange={(e) => renameItem(key, to, { en: il.en, hi: e.target.value })}
                          className="w-24 border border-line bg-surface px-2 py-1 font-deva text-sm text-ink focus:border-saffron focus:outline-none" />
                        <input value={il.en} onChange={(e) => renameItem(key, to, { en: e.target.value, hi: il.hi })}
                          className="w-36 border border-line bg-surface px-2 py-1 text-sm text-ink focus:border-saffron focus:outline-none" />
                        <code className="text-[11px] text-ink-3">{to}{bi?.soon ? " · soon" : ""}</code>
                        <div className="ml-auto flex items-center gap-2">
                          <label className="inline-flex items-center gap-1 text-[11px] text-ink-3">
                            <CornerUpRight className="h-3.5 w-3.5" />
                            <select value={key} onChange={(e) => moveItem(key, to, e.target.value)} data-testid={`nav-item-move-${to.replace(/\//g, "")}`}
                              className="border border-line bg-surface px-1.5 py-1 text-xs text-ink focus:border-saffron focus:outline-none">
                              {NAV.map((g) => <option key={g.key} value={g.key}>{g.label.en}</option>)}
                            </select>
                          </label>
                          <button onClick={() => toggleItem(key, to)} data-testid={`nav-item-hide-${to.replace(/\//g, "")}`} className={itemHidden ? "text-ink-3 hover:text-saffron" : "text-saffron hover:text-ink-2"}>
                            {itemHidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {items.length === 0 && <p className="px-2 py-3 text-xs text-ink-3">Drop items here.</p>}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
